import { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { articles } from '../../mocks/articles';

function renderBodyLine(line: string, idx: number) {
  // H2 heading
  if (line.startsWith('## ')) {
    return (
      <h2 key={idx} className="text-xl lg:text-2xl font-extrabold text-page-ink mt-10 mb-4 tracking-[-0.02em]">
        {line.replace('## ', '')}
      </h2>
    );
  }

  // Bold inline segments
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  const rendered = parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="text-page-ink font-bold">{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });

  return (
    <p key={idx} className="text-page-muted leading-relaxed text-base mb-5">
      {rendered}
    </p>
  );
}

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = articles.find((a) => a.slug === slug);
  const related = articles.filter((a) => a.slug !== slug && a.category === article?.category).slice(0, 2);
  const moreArticles = related.length < 2
    ? [...related, ...articles.filter((a) => a.slug !== slug && a.category !== article?.category)].slice(0, 2)
    : related;

  useEffect(() => {
    if (!article) {
      navigate('/insights', { replace: true });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug, article, navigate]);

  if (!article) return null;

  return (
    <div className="min-h-screen bg-page">
      <Navbar />

      {/* Hero Image */}
      <div className="pt-28 mx-auto px-6 lg:px-10 xl:px-16 max-w-4xl">
        <Link
          to="/insights"
          className="inline-flex items-center gap-2 text-page-faint hover:text-[#c97a2b] text-sm font-medium transition-colors mb-6 cursor-pointer"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-left-line" />
          </div>
          Back to Insights
        </Link>
        <div className="relative w-full h-[280px] lg:h-[380px] overflow-hidden rounded-3xl border border-black/[0.06] shadow-[0_18px_50px_rgba(0,0,0,0.08)] mb-10">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-center"
          />
        </div>

        {/* Category badge */}
        <span className="inline-block px-3 py-1 bg-[#c97a2b]/10 border border-[#c97a2b]/25 text-[#c97a2b] text-xs font-bold rounded-md mb-5 uppercase tracking-wide">
          {article.category}
        </span>

        {/* Title */}
        <h1 className="text-3xl lg:text-4xl font-extrabold text-page-ink mb-6 leading-tight tracking-[-0.03em]">
          {article.title}
        </h1>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 pb-8 mb-8 border-b border-black/[0.08]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#c97a2b]/20 border border-[#c97a2b]/30 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-line text-[#c97a2b] text-sm" />
            </div>
            <div>
              <div className="text-page-ink text-sm font-semibold">{article.author}</div>
              <div className="text-page-faint text-xs">{article.authorRole}</div>
            </div>
          </div>
          <div className="flex items-center gap-1 text-page-faint text-sm">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-line text-xs" />
            </div>
            {article.date}
          </div>
          <div className="flex items-center gap-1 text-page-faint text-sm">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-time-line text-xs" />
            </div>
            {article.readTime}
          </div>
        </div>

        {/* Body */}
        <div className="mb-16">
          {article.body.map((line, idx) => renderBodyLine(line, idx))}
        </div>

        {/* CTA */}
        <div className="bg-white border border-black/[0.08] rounded-2xl p-8 lg:p-10 mb-16 text-center shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          <h3 className="text-xl lg:text-2xl font-extrabold text-page-ink mb-3 tracking-[-0.02em]">
            Ready to take action?
          </h3>
          <p className="text-page-muted text-sm mb-6 max-w-lg mx-auto leading-relaxed">
            Our team of specialists is ready to help you apply these insights to your specific environment. Let&apos;s start a conversation.
          </p>
          <Link to="/contact" className="btn-primary text-sm">
            Talk to an Expert <i className="ri-arrow-right-line text-xs" />
          </Link>
        </div>

        {/* Related Articles */}
        {moreArticles.length > 0 && (
          <div className="mb-16">
            <h3 className="text-lg font-extrabold text-page-ink mb-6 tracking-[-0.02em]">More Articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {moreArticles.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/insights/${rel.slug}`}
                  className="group bg-white rounded-xl border border-black/[0.08] overflow-hidden hover:border-[#c97a2b]/30 hover:-translate-y-1 transition-all duration-300 cursor-pointer shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={rel.image}
                      alt={rel.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <span className="inline-block px-2 py-0.5 bg-[#c97a2b]/10 border border-[#c97a2b]/20 text-[#c97a2b] text-xs font-bold rounded-md mb-3 uppercase tracking-wide">
                      {rel.category}
                    </span>
                    <h4 className="font-bold text-page-ink text-sm leading-tight group-hover:text-[#c97a2b] transition-colors">
                      {rel.title}
                    </h4>
                    <div className="flex items-center gap-1 mt-3 text-[#c97a2b] text-xs font-semibold">
                      Read Article
                      <div className="w-3 h-3 flex items-center justify-center">
                        <i className="ri-arrow-right-line" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
